import { Alert, Autocomplete, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormGroup, Grid, ListItemText, TextField, MenuItem, Select, InputLabel, SelectChangeEvent, ListSubheader, Typography, useTheme } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useState } from 'react';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { InventoryExtensionReportRequest, Store } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import { StoreScope } from '../components/StoreScopePicker';
import AdvancedFilters from '../components/AdvancedFilters';
import dayjs from 'dayjs';
import { ProductType } from '../../../enums/ProductType';
import { filterStoreOptions } from '../../../utils';

// Group months by year for dropdown
const monthOptions = Array.from({ length: 13 }).map((_, idx) => {
  const date = dayjs().subtract(idx, 'month');
  return {
    label: date.format('MMMM'),
    value: date.month() + 1,
    year: date.year(),
  };
});

const groupedMonthOptions = monthOptions.reduce((acc, curr) => {
  acc[curr.year] = acc[curr.year] || [];
  acc[curr.year].push(curr);
  return acc;
}, {} as Record<number, { label: string; value: number; year: number }[]>);

type IProps = {
    /** Fired after a report is successfully queued so the page can refresh status panels. */
    onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
    storeScope?: StoreScope;
    /** Full scope pickers rendered inside the merged Advanced filters collapse. */
    advancedScopeContent?: React.ReactNode;
}

const InventoryExtensionReportForm = (props: IProps) => {
  const { user } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runInventoryExtensionReport } = useNswagClient();
  const { t } = useTranslation('common');
  const theme = useTheme();

  // Prepare product type options from enum
  const productTypeOptions = Object.entries(ProductType)
    .filter(([key, value]) => !isNaN(Number(value)))
    .map(([key, value]) => ({ label: key, value: Number(value) }));

  // Empty selection means "All Item Types" (shown as a chip), same as the Stores/Category pickers.
  const ALL_PRODUCT_TYPES_VALUE = -1;
  const [selectedProductTypes, setSelectedProductTypes] = useState<{ label: string, value: number }[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setSelectedStores([]);

    setCheckedAllStores(checked);
  };


  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleProductTypeChange = (event: SyntheticEvent, value: { label: string, value: number }[]) => {
    // The synthetic "All Item Types" option resets back to the implicit all-types state.
    setSelectedProductTypes(value.some((pt) => pt.value === ALL_PRODUCT_TYPES_VALUE) ? [] : value);
  };

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    const [monthStr, yearStr] = event.target.value.split('-');
    setSelectedMonth(Number(monthStr));
    setSelectedYear(Number(yearStr));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Month and year are required — submitting without them sends a bad request (month: NaN).
    if (!selectedMonth || !selectedYear) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.month-year-required'));
      return;
    }
    setIsGenerating(true);
    setErrorMessage('');

    const selectedAllStores = checkedAllStores || (selectedStores.length > 0 && selectedStores.length == user?.stores?.length);
    const scopedByPicker = !!props.storeScope;

    const year = Number(selectedYear);
    const month = Number(selectedMonth);

    const request: InventoryExtensionReportRequest = {
      reportOnAllAvailableStores: scopedByPicker ? props.storeScope!.storeNumbers.length === 0 : selectedAllStores,
      stores: scopedByPicker
        ? (props.storeScope!.storeNumbers.length > 0 ? props.storeScope!.storeNumbers : undefined)
        : (selectedAllStores ? undefined : selectedStores.map(x => x.storeNumber as string)),
      productTypeIds: selectedProductTypes.length === 0
        ? []
        : selectedProductTypes.map(pt => pt.value),
      month: month,
      year: year,
      tenantId: props.storeScope?.tenantId,
      franchiseId: props.storeScope?.franchiseId,
    };

    runInventoryExtensionReport(request).then(() => {
      setIsRequestSubmitted(true);
      props.onGenerated?.();
    }).catch((error) => {
      props.onGenerationFailed?.(typeof (error) === 'string' ? error : undefined);
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
      setIsRequestSubmitted(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {!props.storeScope && (
          <>
            <Grid item
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={user?.stores ?? []}
                  value={selectedStores}
                  onChange={handleSelectChange}
                  disabled={checkedAllStores}
                  isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('reporting.select-stores')}
                      placeholder={t('reporting.select-stores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllChange} />}
                  label={t('reporting.all-stores')}
                />
              </FormGroup>
            </Grid>
          </>
        )}
        {/* Quick presets stay visible: an "All Item Types" chip. The month/year selector and the
            product types dropdown live under Advanced filters. */}
        <Grid item
          xs={12}
          sx={{ '&&': { paddingTop: '30px' } }}>
          <Typography variant="textMD"
            sx={{ fontWeight: 600, mb: '6px', display: 'block' }}>
            {t('reportingV2.item-types-label')}
          </Typography>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Chip
              label={t('reporting.product-types-all')}
              clickable
              onClick={() => setSelectedProductTypes([])}
              variant={selectedProductTypes.length === 0 ? 'filled' : 'outlined'}
              aria-pressed={selectedProductTypes.length === 0}
              sx={{
                fontWeight: selectedProductTypes.length === 0 ? 600 : 400,
                bgcolor: selectedProductTypes.length === 0 ? theme.palette.primary.main : 'transparent',
                color: selectedProductTypes.length === 0 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                borderColor: selectedProductTypes.length === 0 ? theme.palette.primary.main : theme.palette.custom.gray[300],
                '&:hover': { bgcolor: selectedProductTypes.length === 0 ? theme.palette.primary.dark : theme.palette.custom.gray[100] },
              }}
            />
          </Box>
        </Grid>
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              {props.advancedScopeContent && (
                <Grid item
                  xs={12}>
                  {props.advancedScopeContent}
                </Grid>
              )}
              <Grid item
                sm={6}
                xs={12}>
                <FormControl fullWidth
                  error={showErrors && (!selectedMonth || !selectedYear)}
                  size="small">
                  <InputLabel id="month-select-label">{t('reporting.selectMonth')}</InputLabel>
                  <Select
                    labelId="month-select-label"
                    id="month-select"
                    value={selectedMonth && selectedYear ? `${selectedMonth}-${selectedYear}` : ''}
                    label={t('reporting.selectMonth')}
                    onChange={handleMonthChange}
                    MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                    renderValue={selected => {
                      if (!selected) return '';
                      const [monthStr, yearStr] = String(selected).split('-');
                      const found = monthOptions.find(
                        m => m.value === Number(monthStr) && m.year === Number(yearStr),
                      );
                      return found ? `${found.label} ${found.year}` : '';
                    }}
                  >
                    {Object.keys(groupedMonthOptions)
                      .sort((a, b) => Number(b) - Number(a))
                      .flatMap(year => [
                        <ListSubheader key={year}>{year}</ListSubheader>,
                        ...groupedMonthOptions[Number(year)].map(month => (
                          <MenuItem key={`${month.value}-${month.year}`}
                            value={`${month.value}-${month.year}`}>
                            {month.label}
                          </MenuItem>
                        )),
                      ])}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item
                xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={[{ label: t('reporting.product-types-all'), value: ALL_PRODUCT_TYPES_VALUE }, ...productTypeOptions]}
                    getOptionLabel={option => option.label}
                    value={selectedProductTypes}
                    onChange={handleProductTypeChange}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderOption={(props, option) => {
                      const isAll = option.value === ALL_PRODUCT_TYPES_VALUE;
                      const selected = isAll ? selectedProductTypes.length === 0 : selectedProductTypes.some((pt) => pt.value === option.value);
                      return (
                        <li {...props}
                          key={option.value}>
                          <Checkbox checked={selected}
                            sx={{ mr: 1 }} />
                          <ListItemText primary={option.label} />
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField {...params}
                        label={t('reporting.product-types')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              {selectedProductTypes.length === 0 && (
                                <Chip
                                  label={t('reporting.product-types-all')}
                                  size="small"
                                  sx={{ mr: '4px' }}
                                />
                              )}
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }} />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {
          errorMessage &&
                    <Grid item
                      xs={12}>
                      <Alert severity="error">{errorMessage}</Alert>
                    </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={isGenerating || isRequestSubmitted || (props.storeScope !== undefined && props.storeScope.franchiseId == null)}
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default InventoryExtensionReportForm;